import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { RootState } from "../../../../../redux/store";
import { ScanToContext } from ".";
import {
    ScanToMobilePreview,
    BannerPreviewData,
    OffresPreviewData,
    ActionsPreviewData,
    PublicitesPreviewData,
    CommandePreviewData,
    DiversPreviewData,
    PaiementPreviewData,
    AppelServeurPreviewData,
} from "./View";
import {
    SCAN_TO_DEFAULT_MAIN_COLOR,
    PAYMENT_TAB_INDEX,
    SERVER_CALL_TAB_INDEX,
} from "./shared/constants";
import {
    PreviewSection,
    PreviewToggleButton,
    ClosePreviewButton,
    OpenInNewWindowButton,
    PreviewOverlay,
} from "./SharedPreviewPanel.styled";

// External preview window geometry (portrait phone aspect).
const PREVIEW_WINDOW_WIDTH = 768;
const PREVIEW_WINDOW_HEIGHT = 1024;

type PropsType = {
    activeTab: number;
};

// ─── Single, shared Scan To preview ──────────────────────
// Replaces the eight per-tab copies of <ScanToMobilePreview>. Every section is
// read from Redux (via context), so the preview has ONE source of truth for
// language/direction and data — a tab can no longer desync it (the RTL bug).
// The payment / server-call popups follow the active tab.
export function SharedPreviewPanel({ activeTab }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;
    // ONE language drives the whole preview, including its LTR/RTL direction.
    const previewLanguage = useSelector(
        (state: RootState) => state.scanTo.previewLanguage
    );

    const {
        sharedBannerData,
        sharedOffresData,
        sharedActionsData,
        sharedPublicitesData,
        sharedCommandeData,
        sharedDiversData,
        sharedPaiementData,
        sharedAppelServeurData,
    } = React.useContext(ScanToContext);

    const [previewVisible, setPreviewVisible] = React.useState(true);

    const handleOpenInNewWindow = React.useCallback(() => {
        // Each tab's sync effect already mirrors its section into sessionStorage
        // + the BroadcastChannel, so the external window self-populates; we just
        // open it.
        const left = (window.screen.width - PREVIEW_WINDOW_WIDTH) / 2;
        const top = (window.screen.height - PREVIEW_WINDOW_HEIGHT) / 2;
        window.open(
            "/scan-to-preview",
            "ScanToPreview",
            `width=${PREVIEW_WINDOW_WIDTH},height=${PREVIEW_WINDOW_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
    }, []);

    return (
        <>
            <PreviewToggleButton
                data-testid="scan-to-shared-preview-toggle-btn"
                mainColor={mainColor}
                onClick={() => setPreviewVisible(!previewVisible)}
                title={previewVisible ? t("Hide preview") : t("Show preview")}
                aria-label={
                    previewVisible ? t("Hide preview") : t("Show preview")
                }
                aria-expanded={previewVisible}
            >
                {previewVisible ? (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                )}
            </PreviewToggleButton>

            {/* Decorative click-to-dismiss backdrop (mobile only). Hidden from
                assistive tech — keyboard users close via the toggle / close
                buttons, so it adds nothing for them and shouldn't be focusable. */}
            <PreviewOverlay
                data-testid="scan-to-shared-preview-overlay"
                isVisible={previewVisible}
                onClick={() => setPreviewVisible(false)}
                aria-hidden="true"
            />

            <PreviewSection
                data-testid="scan-to-shared-preview-section"
                isVisible={previewVisible}
            >
                <ClosePreviewButton
                    data-testid="scan-to-shared-preview-close-btn"
                    onClick={() => setPreviewVisible(false)}
                    title={t("Close preview")}
                    aria-label={t("Close preview")}
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </ClosePreviewButton>
                <OpenInNewWindowButton
                    data-testid="scan-to-shared-preview-open-new-window-btn"
                    mainColor={mainColor}
                    onClick={handleOpenInNewWindow}
                    title={t("Open in new window")}
                    aria-label={t("Open in new window")}
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                    </svg>
                </OpenInNewWindowButton>

                <ScanToMobilePreview
                    mainColor={mainColor}
                    selectedLanguage={previewLanguage}
                    bannerData={sharedBannerData as BannerPreviewData | null}
                    offresData={sharedOffresData as OffresPreviewData | null}
                    actionsData={sharedActionsData as ActionsPreviewData | null}
                    publicitesData={
                        sharedPublicitesData as PublicitesPreviewData | null
                    }
                    commandeData={
                        sharedCommandeData as CommandePreviewData | null
                    }
                    diversData={sharedDiversData as DiversPreviewData | null}
                    paiementData={
                        sharedPaiementData as PaiementPreviewData | null
                    }
                    showPaymentPopup={activeTab === PAYMENT_TAB_INDEX}
                    appelServeurData={
                        sharedAppelServeurData as AppelServeurPreviewData | null
                    }
                    showServerCallPopup={activeTab === SERVER_CALL_TAB_INDEX}
                    activeTab={activeTab}
                />
            </PreviewSection>
        </>
    );
}
